import React, { useState, useEffect } from 'react';
import {
  Stack,
  TextInput,
  NumberInput,
  Button,
  Group,
  Switch,
  Select,
  Alert
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { batchService } from '../../services/batchService';
import { curriculumService } from '../../services/curriculumService';
import CustomDateTimePicker from '../common/DateTimePicker';
import dayjs from 'dayjs';

const BatchForm = ({ batch, mode, onSubmit, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [curriculumsLoading, setCurriculumsLoading] = useState(true);
  const [curriculumOptions, setCurriculumOptions] = useState([]);

  const form = useForm({
    initialValues: {
      batch_number: batch?.batch_number || '',
      max_batch_size: batch?.max_batch_size || 50,
      batch_start_time: batch?.batch_start_time ? new Date(batch.batch_start_time) : null,
      cap_time: batch?.cap_time ? new Date(batch.cap_time) : null,
      curriculum_id: batch?.curriculum_id ? batch.curriculum_id.toString() : '',
      bfc_admin_stop: batch?.bfc_admin_stop || false,
      finish_all_live: batch?.finish_all_live || false,
    },
    validate: {
      batch_number: (value) => (!value ? 'Batch number is required' : null),
      max_batch_size: (value) => (value < 1 ? 'Max batch size must be at least 1' : null),
      batch_start_time: (value) => (!value ? 'Start time is required' : null),
      cap_time: (value, values) => {
        if (!value) return 'Cap time is required';
        /* if (values.batch_start_time && value >= values.batch_start_time) {
          return 'Cap time must be before start time';
        } */
        return null;
      },
    },
  });

  useEffect(() => {
    loadCurriculums();
  }, []);

  const loadCurriculums = async () => {
    try {
      setCurriculumsLoading(true);
      const response = await curriculumService.getCurriculums({ per_page: 1000 });

      const options = [
        { value: '', label: 'No Curriculum' },
        ...response.data.map(curriculum => ({
          value: curriculum.id.toString(),
          label: curriculum.title + (curriculum.is_default ? ' (Default)' : '')
        }))
      ];

      setCurriculumOptions(options);
    } catch (error) {
      console.error('Failed to load curriculums:', error);
      notifications.show({
        title: 'Warning',
        message: 'Failed to load curriculums. Using default options.',
        color: 'yellow',
      });

      // Fallback options
      setCurriculumOptions([
        { value: '', label: 'No Curriculum' },
        { value: '1', label: 'Default Curriculum' }
      ]);
    } finally {
      setCurriculumsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formattedValues = {
        batch_number: parseInt(values.batch_number),
        max_batch_size: parseInt(values.max_batch_size),
        batch_start_time: dayjs(values.batch_start_time).format('YYYY-MM-DD HH:mm:ss'),
        cap_time: dayjs(values.cap_time).format('YYYY-MM-DD HH:mm:ss'),
        curriculum_id: values.curriculum_id ? parseInt(values.curriculum_id) : null,
        bfc_admin_stop: values.bfc_admin_stop,
        finish_all_live: values.finish_all_live,
      };

      if (mode === 'create') {
        await batchService.createBatch(formattedValues);
        notifications.show({
          title: 'Success',
          message: 'Batch created successfully',
          color: 'green',
        });
      } else {
        await batchService.updateBatch(batch.id, formattedValues);
        notifications.show({
          title: 'Success',
          message: 'Batch updated successfully',
          color: 'green',
        });
      }
      onSubmit();
    } catch (error) {
      console.error('Failed to save batch:', error);

      let errorMessage = 'Failed to save batch';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors.join(', ');
      }

      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        {mode === 'edit' && batch && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            Editing Batch #{batch.batch_number} - Be careful when modifying active batches
          </Alert>
        )}

        <NumberInput
          label="Batch Number"
          placeholder="Enter batch number"
          required
          min={1}
          {...form.getInputProps('batch_number')}
        />

        <NumberInput
          label="Maximum Batch Size"
          placeholder="Enter maximum number of members"
          min={1}
          required
          {...form.getInputProps('max_batch_size')}
        />

        <CustomDateTimePicker
          label="Batch Start Time"
          placeholder="Select start date and time"
          required
          value={form.values.batch_start_time}
          onChange={(date) => form.setFieldValue('batch_start_time', date)}
          error={form.errors.batch_start_time}
        />

        <CustomDateTimePicker
          label="Cap Time (Enrollment stops before start time)"
          placeholder="Select cap date and time"
          required
          value={form.values.cap_time}
          onChange={(date) => form.setFieldValue('cap_time', date)}
          error={form.errors.cap_time}
        />

        <Select
          label="Curriculum"
          placeholder="Select curriculum for this batch"
          data={curriculumOptions}
          disabled={curriculumsLoading}
          {...form.getInputProps('curriculum_id')}
        />

        {mode === 'edit' && (
          <>
            <Switch
              label="Admin Stop Enrollment"
              description="Manually stop enrollment for this batch"
              {...form.getInputProps('bfc_admin_stop', { type: 'checkbox' })}
            />

            <Switch
              label="Finish All Live Classes"
              description="Mark all live classes in this batch as ended"
              {...form.getInputProps('finish_all_live', { type: 'checkbox' })}
            />
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={loading}
            disabled={curriculumsLoading}
          >
            {mode === 'create' ? 'Create Batch' : 'Update Batch'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default BatchForm;